import { useEffect, useState } from "react";
import { AppLayout } from "../components/Layout/AppLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api from "../lib/api";
import { ROLE_LABELS, useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, RefreshCw, Trash2, FileSpreadsheet, Mail, Send, Plus, Bell } from "lucide-react";
import { suscribirPush } from "../lib/pushNotifications";

const ROLES_CREABLES = ["admin", "jefe_servicio", "encargado", "oficina", "lectura"];

function NewUserDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("encargado");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setEmail("");
      setPassword("");
      setRole("encargado");
    }
  }, [open]);

  const crear = async () => {
    if (!name.trim() || !email.trim() || !password.trim()) {
      toast.error("Completa nombre, email y contraseña");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/users", { name, email, password, role });
      toast.success(`Usuario ${name} creado`);
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el usuario");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="new-user-dialog">
        <DialogHeader>
          <DialogTitle>Nuevo usuario</DialogTitle>
          <DialogDescription>Podrá iniciar sesión con este email y contraseña.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Nombre</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-user-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="new-user-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Contraseña</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="new-user-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Rol</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="new-user-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES_CREABLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r] || r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={crear} disabled={saving} data-testid="save-new-user-button" className="bg-valoriza-leaf text-black hover:bg-valoriza-leaf/90">
            {saving ? "Creando..." : "Crear usuario"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [users, setUsers] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [topics, setTopics] = useState([]);
  const [slaPolicies, setSlaPolicies] = useState([]);
  const [audit, setAudit] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertSettings, setAlertSettings] = useState(null);
  const [syncLog, setSyncLog] = useState([]);
  const [schedulerJobs, setSchedulerJobs] = useState([]);
  const [creditosArcgis, setCreditosArcgis] = useState(null);
  const [purging, setPurging] = useState(false);
  const [syncingGis, setSyncingGis] = useState(false);
  const [generandoIncidencias, setGenerandoIncidencias] = useState(false);
  const [emailSettings, setEmailSettings] = useState(null);
  const [destinatariosTexto, setDestinatariosTexto] = useState("");
  const [emailLog, setEmailLog] = useState([]);
  const [emailPrueba, setEmailPrueba] = useState("");
  const [enviandoPrueba, setEnviandoPrueba] = useState(false);
  const [guardandoEmail, setGuardandoEmail] = useState(false);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [activandoPush, setActivandoPush] = useState(false);

  const activarPush = async () => {
    setActivandoPush(true);
    try {
      await suscribirPush();
      toast.success("Avisos push activados en este navegador");
    } catch (e) {
      toast.error(e.message || "No se pudo activar el aviso push");
    } finally {
      setActivandoPush(false);
    }
  };

  const loadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
      api.get("/admin/audit-log").then((r) => setAudit(r.data)).catch(() => {});
    }
    api.get("/admin/connectors").then((r) => setConnectors(r.data)).catch(() => {});
    api.get("/admin/topics").then((r) => setTopics(r.data)).catch(() => {});
    api.get("/admin/sla-policies").then((r) => setSlaPolicies(r.data)).catch(() => {});
    api.get("/admin/sync-log").then((r) => setSyncLog(r.data)).catch(() => {});
    api.get("/admin/scheduler/jobs").then((r) => setSchedulerJobs(r.data)).catch(() => {});
    api.get("/gis/creditos-uso").then((r) => setCreditosArcgis(r.data)).catch(() => {});
    loadAlerts();
    api.get("/alerts/settings/actuales").then((r) => setAlertSettings(r.data)).catch(() => {});
    loadEmailSettings();
    api.get("/admin/email-log").then((r) => setEmailLog(r.data)).catch(() => {});
  }, [isAdmin]);

  const loadEmailSettings = () =>
    api.get("/admin/email-settings").then((r) => {
      setEmailSettings(r.data);
      setDestinatariosTexto((r.data.destinatarios || []).join(", "));
    }).catch(() => {});

  const guardarEmailSettings = async (cambios) => {
    setGuardandoEmail(true);
    try {
      const payload = { ...emailSettings, ...cambios };
      await api.put("/admin/email-settings", payload);
      setEmailSettings(payload);
      toast.success("Configuración de correo actualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar la configuración de correo");
    } finally {
      setGuardandoEmail(false);
    }
  };

  const guardarDestinatarios = () => {
    const lista = destinatariosTexto.split(",").map((d) => d.trim()).filter(Boolean);
    guardarEmailSettings({ destinatarios: lista });
  };

  const enviarEmailPrueba = async () => {
    if (!emailPrueba) return;
    setEnviandoPrueba(true);
    try {
      await api.post("/admin/email-settings/probar", { to_email: emailPrueba });
      toast.success(`Email de prueba enviado a ${emailPrueba}`);
      api.get("/admin/email-log").then((r) => setEmailLog(r.data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el email de prueba");
    } finally {
      setEnviandoPrueba(false);
    }
  };

  const purgarDemo = async () => {
    if (!window.confirm("¿Eliminar todos los datos DEMO (fichas IRIS, incidencias y rutas de ejemplo)? Los datos reales importados no se verán afectados.")) return;
    setPurging(true);
    try {
      const { data } = await api.post("/admin/demo/purgar");
      toast.success(`Datos DEMO eliminados: ${data.iris_eliminadas} fichas, ${data.incidencias_eliminadas} incidencias, ${data.rutas_eliminadas} rutas`);
    } finally {
      setPurging(false);
    }
  };

  const loadAlerts = () => api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});

  const resolverAlerta = async (id) => {
    await api.patch(`/alerts/${id}/resolver`);
    loadAlerts();
  };

  const evaluarAhora = async () => {
    const { data } = await api.post("/alerts/evaluar-ahora");
    toast.success(`Evaluación completada: ${data.alertas_nuevas} alertas nuevas`);
    loadAlerts();
  };

  const guardarAlertSettings = async () => {
    await api.put("/alerts/settings/actuales", alertSettings);
    toast.success("Umbrales de alerta actualizados");
  };

  const testConnection = async () => {
    const { data } = await api.post("/gis/test-connection");
    toast[data.conectado ? "success" : "warning"](data.mensaje);
  };

  const sincronizarGisReal = async () => {
    if (!window.confirm("Esto sustituirá los sectores/barrios sintéticos por los polígonos reales del GIS y reasignará todas las fichas IRIS. ¿Continuar?")) return;
    setSyncingGis(true);
    try {
      const { data } = await api.post("/gis/sincronizar-reales");
      toast.success(`GIS real sincronizado: ${data.barrios_importados} barrios, ${data.sectores_importados} sectores, ${data.fichas_reasignadas} fichas y ${data.incidencias_reasignadas} incidencias reasignadas`);
      api.get("/admin/sync-log").then((r) => setSyncLog(r.data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al sincronizar el GIS real");
    } finally {
      setSyncingGis(false);
    }
  };

  const generarIncidenciasCriticas = async () => {
    setGenerandoIncidencias(true);
    try {
      const { data } = await api.post("/iris/generar-incidencias-criticas");
      toast.success(`Incidencias generadas: ${data.creadas} nuevas de ${data.candidatas_evaluadas} fichas evaluadas`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar incidencias críticas");
    } finally {
      setGenerandoIncidencias(false);
    }
  };

  const exportarSyncLog = async () => {
    const { data } = await api.get("/admin/sync-log/exportar", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "historial_sincronizaciones.xlsx");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const toggleTopic = async (id, activo) => {
    await api.patch(`/admin/topics/${id}`, { activo });
    setTopics((t) => t.map((x) => (x._id === id ? { ...x, activo } : x)));
  };

  const updateSla = async (id, plazo_horas) => {
    await api.patch(`/admin/sla-policies/${id}`, { plazo_horas: Number(plazo_horas) });
  };

  const toggleUserActive = async (id, active) => {
    await api.patch(`/admin/users/${id}`, { active });
    setUsers((u) => u.map((x) => (x._id === id ? { ...x, active } : x)));
    toast.success(active ? "Usuario activado" : "Usuario desactivado");
  };

  const exportarCreditosArcgis = async () => {
    try {
      const { data } = await api.get("/gis/creditos-uso/exportar", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "creditos_arcgis.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo exportar el historial de créditos");
    }
  };

  return (
    <AppLayout title="Administración">
      <Tabs defaultValue={isAdmin ? "usuarios" : "conectores"}>
        <TabsList data-testid="admin-tabs">
          {isAdmin && <TabsTrigger value="usuarios" data-testid="tab-usuarios">Usuarios</TabsTrigger>}
          <TabsTrigger value="conectores" data-testid="tab-conectores">Conectores</TabsTrigger>
          <TabsTrigger value="tematicas" data-testid="tab-tematicas">Temáticas</TabsTrigger>
          <TabsTrigger value="sla" data-testid="tab-sla">SLA</TabsTrigger>
          <TabsTrigger value="alertas" data-testid="tab-alertas">Alertas</TabsTrigger>
          <TabsTrigger value="correo" data-testid="tab-correo">Correo</TabsTrigger>
          {isAdmin && <TabsTrigger value="auditoria" data-testid="tab-auditoria">Auditoría</TabsTrigger>}
        </TabsList>

        {isAdmin && (
        <TabsContent value="usuarios" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setNewUserOpen(true)} data-testid="new-user-button" className="gap-2 bg-valoriza-leaf text-black hover:bg-valoriza-leaf/90">
              <Plus className="h-4 w-4" /> Nuevo usuario
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Activo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="admin-users-table">
                  {users.map((u) => (
                    <TableRow key={u._id}>
                      <TableCell className="font-bold">{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{ROLE_LABELS[u.role]}</TableCell>
                      <TableCell>
                        <Switch checked={u.active} onCheckedChange={(v) => toggleUserActive(u._id, v)} data-testid={`user-active-switch-${u._id}`} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        <TabsContent value="conectores" className="space-y-4">
          <div className="flex justify-end gap-2 flex-wrap">
            {isAdmin && (
              <Button onClick={sincronizarGisReal} disabled={syncingGis} variant="outline" data-testid="sync-gis-real-button" className="gap-2">
                <RefreshCw className={`h-4 w-4 ${syncingGis ? "animate-spin" : ""}`} /> {syncingGis ? "Sincronizando GIS..." : "Migrar sectores a GIS real"}
              </Button>
            )}
            <Button onClick={generarIncidenciasCriticas} disabled={generandoIncidencias} variant="outline" data-testid="generate-critical-incidents-button" className="gap-2">
              {generandoIncidencias ? "Generando..." : "Generar incidencias críticas"}
            </Button>
            {isAdmin && (
              <Button onClick={purgarDemo} disabled={purging} variant="outline" data-testid="purge-demo-button" className="gap-2 text-accent-alert border-accent-alert/40 hover:bg-accent-alert/10">
                <Trash2 className="h-4 w-4" /> {purging ? "Eliminando..." : "Eliminar datos DEMO"}
              </Button>
            )}
          </div>
          {connectors.map((c) => (
            <Card key={c.connector_key} data-testid={`connector-card-${c.connector_key}`}>
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="font-bold">{c.nombre}</p>
                  <p className="text-xs text-muted-foreground mt-1">{c.mensaje_estado}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={c.estado === "activo" ? "outline" : c.estado === "error" ? "destructive" : "secondary"}>{c.estado}</Badge>
                  {c.connector_key === "A" && (
                    <button onClick={testConnection} data-testid="test-arcgis-connection-button" className="text-xs underline text-valoriza-leaf font-bold">
                      Probar GIS
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          <Card>
            <CardHeader>
              <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Automatizaciones activas</p>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="scheduler-jobs-list">
              {schedulerJobs.length === 0 && <p className="text-xs text-muted-foreground">Sin trabajos programados.</p>}
              {schedulerJobs.map((j) => (
                <div key={j.id} className="flex items-center justify-between border-b border-border pb-2 last:border-0" data-testid={`scheduler-job-${j.id}`}>
                  <div>
                    <p className="text-sm font-medium">{j.nombre}</p>
                    <p className="text-xs text-muted-foreground">{j.trigger}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline">activo</Badge>
                    {j.proxima_ejecucion && (
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Próxima: {new Date(j.proxima_ejecucion).toLocaleString("es-ES")}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Créditos ArcGIS Online (rutas)</p>
            </CardHeader>
            <CardContent className="space-y-2" data-testid="arcgis-credits-card">
              {creditosArcgis ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Últimos 30 días</p>
                      <p className="text-xl font-black text-valoriza-leaf" data-testid="arcgis-credits-30d">
                        {creditosArcgis.creditos_ultimos_30_dias} créditos ({creditosArcgis.llamadas_ultimos_30_dias} rutas)
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total desde siempre</p>
                      <p className="text-xl font-black" data-testid="arcgis-credits-total">
                        {creditosArcgis.creditos_totales_estimados} créditos ({creditosArcgis.llamadas_totales} rutas)
                      </p>
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2">{creditosArcgis.nota}</p>
                  <Button onClick={exportarCreditosArcgis} size="sm" variant="outline" className="gap-2 mt-2" data-testid="export-arcgis-credits-button">
                    <FileSpreadsheet className="h-3.5 w-3.5" /> Exportar historial XLSX
                  </Button>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">Cargando...</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Historial de sincronizaciones</p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="flex justify-end px-4 pt-4">
                <Button onClick={exportarSyncLog} size="sm" variant="outline" data-testid="export-sync-log-button" className="gap-2">
                  <FileSpreadsheet className="h-3.5 w-3.5" /> Exportar Excel
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Conector</TableHead>
                    <TableHead>Resultado</TableHead>
                    <TableHead>Fichas nuevas</TableHead>
                    <TableHead>Duración</TableHead>
                    <TableHead>Mensaje</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="sync-log-table">
                  {syncLog.map((s) => (
                    <TableRow key={s._id}>
                      <TableCell className="text-xs">{new Date(s.fecha).toLocaleString("es-ES")}</TableCell>
                      <TableCell className="text-xs">{s.conector}</TableCell>
                      <TableCell>
                        <Badge variant={s.resultado === "ok" ? "outline" : "destructive"}>{s.resultado}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">{s.fichas_nuevas}</TableCell>
                      <TableCell className="text-xs">{s.duracion_seg}s</TableCell>
                      <TableCell className="text-xs max-w-sm truncate">{s.mensaje}</TableCell>
                    </TableRow>
                  ))}
                  {syncLog.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-xs text-muted-foreground text-center py-6">
                        Sin sincronizaciones registradas todavía.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tematicas">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Área</TableHead>
                    <TableHead>Elemento</TableHead>
                    <TableHead>Detalle</TableHead>
                    <TableHead>Activo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="admin-topics-table">
                  {topics.map((t) => (
                    <TableRow key={t._id}>
                      <TableCell>{t.area}</TableCell>
                      <TableCell>{t.elemento}</TableCell>
                      <TableCell>{t.detalle}</TableCell>
                      <TableCell>
                        <Switch checked={t.activo} onCheckedChange={(v) => toggleTopic(t._id, v)} data-testid={`topic-switch-${t._id}`} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sla">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Temática</TableHead>
                    <TableHead>Plazo (horas)</TableHead>
                    <TableHead>Escalado %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="admin-sla-table">
                  {slaPolicies.map((s) => (
                    <TableRow key={s._id}>
                      <TableCell>{s.tematica}</TableCell>
                      <TableCell>
                        <Input type="number" defaultValue={s.plazo_horas} className="w-24 h-8" onBlur={(e) => updateSla(s._id, e.target.value)} data-testid={`sla-plazo-${s._id}`} />
                      </TableCell>
                      <TableCell>{s.escalado_pct}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alertas" className="space-y-4">
          <Card>
            <CardContent className="p-5 flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Ventana (días)</label>
                <Input
                  type="number"
                  className="w-28"
                  value={alertSettings?.ventana_dias ?? ""}
                  onChange={(e) => setAlertSettings((s) => ({ ...s, ventana_dias: Number(e.target.value) }))}
                  data-testid="alert-settings-ventana-dias"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Umbral quejas/sector</label>
                <Input
                  type="number"
                  className="w-28"
                  value={alertSettings?.umbral_quejas ?? ""}
                  onChange={(e) => setAlertSettings((s) => ({ ...s, umbral_quejas: Number(e.target.value) }))}
                  data-testid="alert-settings-umbral-quejas"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Inactividad GPS (min)</label>
                <Input
                  type="number"
                  className="w-28"
                  value={alertSettings?.umbral_inactividad_gps_min ?? ""}
                  onChange={(e) => setAlertSettings((s) => ({ ...s, umbral_inactividad_gps_min: Number(e.target.value) }))}
                  data-testid="alert-settings-umbral-inactividad-gps"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Turno desde</label>
                <Input
                  type="time"
                  className="w-28"
                  value={alertSettings?.horario_turno_inicio ?? ""}
                  onChange={(e) => setAlertSettings((s) => ({ ...s, horario_turno_inicio: e.target.value }))}
                  data-testid="alert-settings-horario-inicio"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Turno hasta</label>
                <Input
                  type="time"
                  className="w-28"
                  value={alertSettings?.horario_turno_fin ?? ""}
                  onChange={(e) => setAlertSettings((s) => ({ ...s, horario_turno_fin: e.target.value }))}
                  data-testid="alert-settings-horario-fin"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Créditos ArcGIS/mes</label>
                <Input
                  type="number"
                  className="w-28"
                  value={alertSettings?.umbral_creditos_arcgis_mensual ?? ""}
                  onChange={(e) => setAlertSettings((s) => ({ ...s, umbral_creditos_arcgis_mensual: Number(e.target.value) }))}
                  data-testid="alert-settings-umbral-creditos-arcgis"
                />
              </div>
              <Button onClick={guardarAlertSettings} data-testid="save-alert-settings-button" size="sm" className="bg-valoriza-leaf text-black hover:bg-valoriza-leaf/90">
                Guardar umbrales
              </Button>
              <Button onClick={evaluarAhora} data-testid="evaluate-alerts-now-button" size="sm" variant="outline" className="gap-2">
                <RefreshCw className="h-3.5 w-3.5" /> Evaluar ahora
              </Button>
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground -mt-2">
            La alerta de inactividad GPS avisa cuando un encargado que ya ha enviado su posición hoy lleva más del umbral indicado sin enviar una nueva, dentro de la franja de turno configurada. Se resuelve sola en cuanto vuelve a enviar su posición. La alerta de créditos ArcGIS avisa una vez al mes si el consumo estimado de rutas automáticas supera el umbral configurado.
          </p>
          {(user?.role === "jefe_servicio" || user?.role === "admin") && (
            <Card>
              <CardContent className="p-5 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-bold">Avisos push en el navegador</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Recibe una notificación en este navegador cuando salte una alerta de inactividad GPS de un encargado.
                  </p>
                </div>
                <Button onClick={activarPush} disabled={activandoPush} data-testid="activate-push-button" size="sm" variant="outline" className="gap-2 whitespace-nowrap">
                  <Bell className="h-3.5 w-3.5" /> {activandoPush ? "Activando..." : "Activar avisos push"}
                </Button>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nivel</TableHead>
                    <TableHead>Mensaje</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="admin-alerts-table">
                  {alerts.map((a) => (
                    <TableRow key={a._id}>
                      <TableCell>
                        <Badge variant={a.nivel === "critical" ? "destructive" : "secondary"}>{a.nivel}</Badge>
                      </TableCell>
                      <TableCell className="text-xs max-w-md">{a.mensaje}</TableCell>
                      <TableCell className="text-xs">{new Date(a.created_at).toLocaleString("es-ES")}</TableCell>
                      <TableCell>
                        <Badge variant={a.resuelto ? "outline" : "destructive"}>{a.resuelto ? "Resuelta" : "Activa"}</Badge>
                      </TableCell>
                      <TableCell>
                        {!a.resuelto && (
                          <button onClick={() => resolverAlerta(a._id)} data-testid={`resolve-admin-alert-${a._id}`}>
                            <CheckCircle2 className="h-4 w-4 text-muted-foreground hover:text-valoriza-leaf" />
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="correo" className="space-y-4">
          <Card data-testid="email-settings-card">
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <Mail className="h-4 w-4" /> Notificaciones por correo (Mandrill)
              </p>
              {emailSettings && (
                <Badge variant={emailSettings.mandrill_configurado ? "outline" : "destructive"}>
                  {emailSettings.mandrill_configurado ? "Mandrill configurado" : "Mandrill sin configurar"}
                </Badge>
              )}
            </CardHeader>
            <CardContent className="space-y-5">
              {emailSettings && (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Notificaciones activas</p>
                      <p className="text-xs text-muted-foreground">Activa o desactiva el envío real de correos (las alertas seguirán apareciendo en la campana igualmente).</p>
                    </div>
                    <Switch
                      checked={emailSettings.activo}
                      onCheckedChange={(v) => guardarEmailSettings({ activo: v })}
                      disabled={!isAdmin}
                      data-testid="email-active-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Alertas de umbral</p>
                      <p className="text-xs text-muted-foreground">Enviar email cuando se genere una alerta de quejas o SLA.</p>
                    </div>
                    <Switch
                      checked={emailSettings.notificar_alertas}
                      onCheckedChange={(v) => guardarEmailSettings({ notificar_alertas: v })}
                      disabled={!isAdmin}
                      data-testid="email-notify-alerts-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Resumen semanal</p>
                      <p className="text-xs text-muted-foreground">Enviar email con el resumen semanal de sectores críticos (cada lunes).</p>
                    </div>
                    <Switch
                      checked={emailSettings.notificar_resumen_semanal}
                      onCheckedChange={(v) => guardarEmailSettings({ notificar_resumen_semanal: v })}
                      disabled={!isAdmin}
                      data-testid="email-notify-weekly-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Incluir encargado del sector afectado</p>
                      <p className="text-xs text-muted-foreground">Además de la lista fija, avisa también al encargado responsable del sector de la alerta.</p>
                    </div>
                    <Switch
                      checked={emailSettings.incluir_responsables_sector}
                      onCheckedChange={(v) => guardarEmailSettings({ incluir_responsables_sector: v })}
                      disabled={!isAdmin}
                      data-testid="email-include-responsables-switch"
                    />
                  </div>

                  <div className="space-y-1.5 pt-2 border-t border-border">
                    <p className="text-sm font-medium">Destinatarios fijos</p>
                    <p className="text-xs text-muted-foreground mb-1.5">Correos separados por comas (ej: jefe.servicio@valorizasm.com, admin@valorizasm.com)</p>
                    <div className="flex gap-2">
                      <Input
                        value={destinatariosTexto}
                        onChange={(e) => setDestinatariosTexto(e.target.value)}
                        placeholder="correo1@valorizasm.com, correo2@valorizasm.com"
                        disabled={!isAdmin}
                        data-testid="email-recipients-input"
                      />
                      <Button onClick={guardarDestinatarios} disabled={guardandoEmail || !isAdmin} variant="outline" data-testid="email-recipients-save-button">
                        Guardar
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-1.5 pt-2 border-t border-border">
                    <p className="text-sm font-medium">Enviar email de prueba</p>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        value={emailPrueba}
                        onChange={(e) => setEmailPrueba(e.target.value)}
                        placeholder="tu-correo@valorizasm.com"
                        data-testid="email-test-input"
                      />
                      <Button onClick={enviarEmailPrueba} disabled={enviandoPrueba || !emailPrueba} className="gap-2" data-testid="email-test-send-button">
                        <Send className="h-3.5 w-3.5" /> {enviandoPrueba ? "Enviando..." : "Enviar prueba"}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Historial de envíos</p>
            </CardHeader>
            <CardContent className="p-0 max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Destinatarios</TableHead>
                    <TableHead>Asunto</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="email-log-table">
                  {emailLog.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-xs text-muted-foreground text-center py-4">
                        Todavía no se ha enviado ningún email.
                      </TableCell>
                    </TableRow>
                  )}
                  {emailLog.map((e) => (
                    <TableRow key={e._id}>
                      <TableCell className="text-xs">{new Date(e.created_at).toLocaleString("es-ES")}</TableCell>
                      <TableCell className="text-xs">{e.tipo}</TableCell>
                      <TableCell className="text-xs">{(e.destinatarios || []).join(", ")}</TableCell>
                      <TableCell className="text-xs">{e.asunto}</TableCell>
                      <TableCell>
                        <Badge variant={e.estado === "enviado" ? "outline" : e.estado === "error" ? "destructive" : "secondary"}>{e.estado}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>


        {isAdmin && (
        <TabsContent value="auditoria">
          <Card>
            <CardContent className="p-0 max-h-[500px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Acción</TableHead>
                    <TableHead>Detalle</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="admin-audit-table">
                  {audit.map((a) => (
                    <TableRow key={a._id}>
                      <TableCell className="text-xs">{new Date(a.timestamp).toLocaleString("es-ES")}</TableCell>
                      <TableCell className="text-xs">{a.usuario_nombre}</TableCell>
                      <TableCell className="text-xs">{a.accion}</TableCell>
                      <TableCell className="text-xs">{a.detalle}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      <NewUserDialog open={newUserOpen} onClose={() => setNewUserOpen(false)} onCreated={loadUsers} />
    </AppLayout>
  );
}
